import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import {
  EnabledLanguage,
  InternationalTplPageInfo,
  InternationalTplUser,
  InternationalTplVehicleType,
  IntTplOfferResponse,
} from '../domain/types'
import { PageInfo } from '../components/PageHeader'

export type IntTplState = {
  filter: {
    isoCode: string
    day: number | null
    startDate: string | null
    insurancePeriodId: number | null
  }
  checkout: {
    user: Partial<InternationalTplUser>
    vehicle: Partial<InternationalTplVehicleType>
    checkMyCar: boolean
    carCountdown: {
      minute: number
      second: number
    }
    offer: Partial<IntTplOfferResponse>
  }
  screenHeader: {
    totalCount: number
    passedCount: number
  }
  pages: PageInfo[]
  pageData: Record<string, InternationalTplPageInfo>
}

const realInitialState: IntTplState = {
  filter: {
    isoCode: '',
    day: null,
    startDate: null,
    insurancePeriodId: null,
  },
  checkout: {
    user: {
      clientType: 'individual',
    },
    vehicle: {},
    checkMyCar: false,
    carCountdown: {
      minute: 0,
      second: 0,
    },
    offer: {},
  },
  screenHeader: {
    totalCount: 7,
    passedCount: 1,
  },
  pages: [],
  pageData: {},
}

const initialState = realInitialState

const slice = createSlice({
  name: 'internationalTplSlice',
  initialState,
  reducers: {
    clearInternationaTplStore: (state: IntTplState, actionPayload: PayloadAction<undefined>) => {
      return {
        ...initialState,
      }
    },

    setFilterField: <K extends keyof IntTplState['filter']>(
      state: IntTplState,
      action: PayloadAction<{ fieldName: K; fieldValue: IntTplState['filter'][K] }>,
    ) => {
      const { fieldName, fieldValue } = action.payload
      state.filter[fieldName] = fieldValue
    },

    setUserField: (
      state: IntTplState,
      action: PayloadAction<{ fieldName: keyof InternationalTplUser; fieldValue: any }>,
    ) => {
      const { fieldName, fieldValue } = action.payload
      state.checkout.user[fieldName] = fieldValue
    },

    setUserTranslationField: (
      state: IntTplState,
      action: PayloadAction<{
        fieldName: keyof { firstName: string; lastName: string }
        language: EnabledLanguage
        fieldValue: any
      }>,
    ) => {
      const { fieldName, fieldValue, language } = action.payload
      state.checkout.user = {
        ...state.checkout.user,
        translations: {
          ...state.checkout.user.translations,
          [language]: {
            ...state.checkout.user.translations?.[language],
            [fieldName]: fieldValue,
          },
        },
      }
    },

    setVehicleField: (
      state: IntTplState,
      action: PayloadAction<{ fieldName: keyof InternationalTplVehicleType; fieldValue: any }>,
    ) => {
      const { fieldName, fieldValue } = action.payload
      state.checkout.vehicle[fieldName] = fieldValue
    },

    setCheckMyCar: (state: IntTplState, action: PayloadAction<boolean>) => {
      state.checkout.checkMyCar = action.payload
    },

    setUpdateCarCountdown: (state: IntTplState, action: PayloadAction<IntTplState['checkout']['carCountdown']>) => {
      state.checkout.carCountdown = action.payload
    },

    setOffer: (state: IntTplState, { payload: offer }: PayloadAction<IntTplState['checkout']['offer']>) => {
      state.checkout.offer = offer
    },

    setScreenHeader: (
      state: IntTplState,
      action: PayloadAction<{ totalCount?: number; passedCount?: number }>,
    ) => {
      if (action.payload.totalCount !== undefined) {
        state.screenHeader.totalCount = action.payload.totalCount
      }
      if (action.payload.passedCount !== undefined) {
        state.screenHeader.passedCount = action.payload.passedCount
      }
    },

    setPages: (state: IntTplState, action: PayloadAction<PageInfo[]>) => {
      state.pages = action.payload
    },

    setPageData: (
      state: IntTplState,
      action: PayloadAction<{ slug: string; data: InternationalTplPageInfo }>,
    ) => {
      state.pageData[action.payload.slug] = action.payload.data
    },

    setAllPageData: (state: IntTplState, action: PayloadAction<Record<string, InternationalTplPageInfo>>) => {
      state.pageData = action.payload
    },
  },
})

export const {
  clearInternationaTplStore,
  setFilterField,
  setUserField,
  setUserTranslationField,
  setVehicleField,
  setCheckMyCar,
  setUpdateCarCountdown,
  setOffer,
  setScreenHeader,
  setPages,
  setPageData,
  setAllPageData,
} = slice.actions

export default slice.reducer
